import {
  Button, Checkbox, FormControl, FormControlLabel,
  FormHelperText, Grid, InputLabel, TextField,
  Typography, withStyles, WithStyles
} from '@material-ui/core';
import { isValidPhoneNumber } from 'react-phone-number-input';
import MuiPhoneInput from '../../components/Form/MuiPhoneInput';
import React, { Fragment, useState, useEffect } from 'react';
import { withLocale } from '../../hocs/withLocale';
import { compose } from 'recompose';
import { serviceLocator } from '../../infrastructure/ServiceLocator';
import LayoutHelper from '../../infrastructure/LayoutHelper';
import { SignUpConfirmationButtons } from './SignUpConfirmationButtons';
import { withFormik, FormikProps, FormikErrors } from 'formik';
import { BookingTermsAndConditions } from './BookingTermsAndConditions';
import SnackbarContent from '@material-ui/core/SnackbarContent';
import { Recaptcha } from '../../components/Shared/Recaptcha';
import { withSnackbar, WithSnackbarProps } from 'notistack';
import InfoAlert from '../../components/Alerts/InfoAlert';
import { Theme, createStyles } from '@material-ui/core/styles';
import { ClassInfo, Owner, Studio, Payment, StudioClient } from './SignUpConfirmationButtons';

interface FormFieldStyles {
  field: string;
  label: string;
}

interface FormFieldProps extends WithStyles<typeof formFieldStyles> {
  label: string;
  name: string;
  errors: FormikErrors<FormValues>;
  children: React.ReactNode;
}

const formFieldStyles = (theme: Theme) => createStyles({
  field: {
    display: 'flex',
    flexDirection: 'column',
    margin: `${theme.spacing(2)}px 0`,
  },
  label: {
    marginBottom: theme.spacing(1),
    position: 'relative',
    transform: 'none',
    display: 'flex',
    alignItems: 'center',
  },
});

const FormField = withStyles(formFieldStyles)(({ label, name, errors, children, classes }: FormFieldProps) => {
  const error = errors[name];
  return (
    <FormControl className={classes.field} error={!!error}>
      <InputLabel className={classes.label}>
        {label}
        {error && <FormHelperText>&nbsp;{error}</FormHelperText>}
      </InputLabel>
      {children}
    </FormControl>
  );
});

interface GuestBookingFormStyles {
  phoneInput: string;
  containerWidth: string;
  confirmed: string;
  buttons: string;
  errorMessage: string;
}

const guestBookingFormStyles = (theme: Theme) => createStyles({
  phoneInput: {
    marginTop: theme.spacing(1.5),
  },
  containerWidth: { width: 'auto' },
  confirmed: {
    height: theme.spacing(10),
    color: 'green',
    display: 'flex',
    alignItems: 'center',
  },
  buttons: {
    [theme.breakpoints.down('xs')]: {
      position: 'fixed',
      left: 0,
      bottom: 0,
      width: '100%',
    },
  },
  errorMessage: {
    margin: '5px 0px'
  }
});

interface FormValues {
  domain: {
    id: string;
    countryCode: string;
  };
  withFriends: boolean;
  phone?: string;
  clientName?: string;
  clientEmail?: string;
  clientNote?: string;
  studioClient?: StudioClient;
  pendingClient?: PendingClient;
  pin?: string;
  phoneConfirmed?: boolean;
  tcAccepted?: boolean;
  captcha?: boolean;
  numberOfFriends?: number;
  errors?: string[];
}

interface PendingClient {
  _id: string;
  [key: string]: any;
}

interface Course {
  repeatableEvent?: {
    beforeBookingText?: string;
  };
  [key: string]: any;
}

interface GuestBookingFormProps extends WithStyles<typeof guestBookingFormStyles>, WithSnackbarProps {
  course?: Course;
  spotsLeft: number;
  owner: Owner;
  studio: Studio;
  classInfo: ClassInfo;
  setAttendance: (attendance: any) => void;
  onSuccess: (attendance: any) => void;
  onFailure: (error: any) => void;
  translate: (key: string, data?: any) => string;
  domain: {
    domain: {
      id: string;
      countryCode: string;
    };
  };
}

interface GuestBookingFormEnhancedProps extends GuestBookingFormProps, FormikProps<FormValues> {}

const validateForm = (values: FormValues, props: GuestBookingFormProps): FormikErrors<FormValues> => {
  let errors: FormikErrors<FormValues> = {};
  let mandatoryFields: (keyof FormValues)[] = ['phone'];

  if (!values.studioClient) {
    if (values.clientEmail && !LayoutHelper.isEmailValid(values.clientEmail)) {
      errors.clientEmail = props.translate('common.invalidEmail');
    }

    mandatoryFields.push('tcAccepted');
    mandatoryFields.push('captcha');
    mandatoryFields.push('clientName');

    if (props.studio.requestEmailFromGuest) {
      mandatoryFields.push('clientEmail');
    }
  }

  if (values.phone && !isValidPhoneNumber(values.phone)) {
    errors.phone = props.translate('common.invalidPhone');
  }

  mandatoryFields.forEach((field) => {
    if (!values[field]) {
      errors[field] = '*';
    }
  });

  return errors;
};

export const GuestBookingForm = compose<GuestBookingFormEnhancedProps, GuestBookingFormProps>(
  withLocale,
  withSnackbar,
  withStyles(guestBookingFormStyles),
  withFormik<GuestBookingFormProps, FormValues>({
    mapPropsToValues: (props) => ({
      domain: props.domain.domain,
      withFriends: false,
    }),
    validate: validateForm,
    displayName: 'BookingClassInfoForm',
  })
)(
  ({
     course,
     spotsLeft,
     owner,
     studio,
     classInfo,
     setAttendance,
     onSuccess,
     onFailure,
     values,
     errors,
     handleChange,
     isSubmitting,
     setSubmitting,
     translate,
     classes,
     enqueueSnackbar,
   }) => {
    const [generatingPin, setGeneratingPin] = useState<boolean>(false);
    const [showInfoMessage, setShowInfoMessage] = useState<boolean>(false);
    const isPhoneValid = values.phone && isValidPhoneNumber(values.phone);

    const setCustomErrors = (customErrors: string[]) => handleChange('errors')(customErrors);

    const resetClient = ({ id, name, email }: { id: string; name: string; email: string }) => {
      handleChange('clientName')(name);
      handleChange('clientEmail')(email);
      handleChange('pin')(undefined);
    };

    const generatePin = (e?: React.MouseEvent, phone?: string) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      setGeneratingPin(true);
      setShowInfoMessage(true);

      if (values.studioClient) {
        const api = serviceLocator.clientApi;
        api.generatePin(studio.owner_id, values.studioClient._id)
          .then(() => setGeneratingPin(false))
          .catch(err => {
            setGeneratingPin(false);
            enqueueSnackbar("Помилка при генерації PIN", { variant: 'error' });
          });
      } else {
        const api = serviceLocator.pendingClientApi;
        if (phone) {
          api.generatePin(studio.owner_id, phone)
            .then((pendingClient: PendingClient) => {
              handleChange('pendingClient')(pendingClient);
              setGeneratingPin(false);
            })
            .catch(err => {
              setGeneratingPin(false);
              enqueueSnackbar("Помилка при генерації PIN", { variant: 'error' });
            });
        }
      }
    };

    const getClient = (phone: string) => {
      if (!(phone && isValidPhoneNumber(phone))) return;

      setSubmitting(true);
      const api = serviceLocator.clientApi;

      api.checkPhone(studio.owner_id, phone)
        .then((data: { clients: StudioClient[] }) => {
          if (data && data.clients && data.clients.length > 0) {
            const studioClient = data.clients[0];
            handleChange('studioClient')(studioClient);
          } else {
            generatePin(undefined, phone);
          }
          setSubmitting(false);
        })
        .catch(err => {
          setSubmitting(false);
          enqueueSnackbar("Помилка при перевірці телефону", { variant: 'error' });
        });
    };

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      let value = e.target.value?.trim() || '';
      handleChange('clientEmail')(value);
    };

    const onPinChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
      const pin = e.target.value;
      handleChange('pin')(pin);

      if (!pin || pin.length !== 4 || isNaN(parseInt(pin))) {
        return;
      }

      setSubmitting(true);

      if (values.studioClient) {
        verifyStudioClientPin(pin);
      } else {
        verifyPendingClientPin(pin);
      }
    };

    const verifyStudioClientPin = (pin: string) => {
      const api = serviceLocator.clientApi;
      if (!values.phone) return;
      
      api.checkPhonePin(studio.owner_id, values.phone, pin)
        .then((studioClientData: { client: StudioClient }) => {
          const studioClient = studioClientData.client;
          if (studioClient) {
            resetClient(studioClient);
            handleChange('phoneConfirmed')(true);
            fetchAttendanceData();
          } else {
            handleChange('phoneConfirmed')(false);
          }
          setSubmitting(false);
        })
        .catch(err => {
          setSubmitting(false);
          enqueueSnackbar("Помилка при перевірці PIN коду", { variant: 'error' });
        });
    };

    const verifyPendingClientPin = (pin: string) => {
      const api = serviceLocator.pendingClientApi;
      if (!values.pendingClient || !values.phone) return;
      
      api.validatePin(studio.owner_id, values.pendingClient._id, values.phone, pin)
        .then((studioClientData: { client: { id: string; name: string; email: string } }) => {
          const client = studioClientData.client;
          if (client) {
            resetClient(client);
            handleChange('phoneConfirmed')(true);
          } else {
            handleChange('phoneConfirmed')(false);
          }
          setSubmitting(false);
        })
        .catch(err => {
          setSubmitting(false);
          enqueueSnackbar("Помилка при перевірці PIN коду", { variant: 'error' });
        });
    };

    const fetchAttendanceData = () => {
      if (!values.phone) return;
      
      serviceLocator.onlineRegistrationsApiCall
        .studioClientsByPhone(studio.id, values.phone, classInfo.groupId)
        .then((data: { attendance: any; client: any }) => {
          const { attendance, client } = data;
          if (attendance) {
            setAttendance(attendance);
          }
        })
        .catch(LayoutHelper.onErr);
    };

    const signUp = async (payment?: Payment) => {
      setSubmitting(true);
      try {
        const attendance = await serviceLocator.registrationApi.noPinSignUp(
          studio.id,
          classInfo.groupId,
          {
            domain: values.domain.id,
            phone: values.phone,
            email: values.clientEmail,
            name: values.clientName,
            note: values.clientNote,
            numberOfFriends: values.numberOfFriends,
          },
          payment,
          classInfo.attendancePrice
        );
        onSuccess(attendance);
      } catch (e) {
        onFailure(e);
      }
      setSubmitting(false);
    };

    const onPaymentFailed = async () => {
      enqueueSnackbar("Не вдається обробити платіж", {
        persist: true,
        variant: 'error',
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left',
        },
      });
      setSubmitting(false);
    };

    useEffect(() => {
      return () => {};
    }, []);

    if (values.errors) {
      return values.errors.map((e, index) => (
        <SnackbarContent key={index} message={e} />
      ));
    }

    const isConfirmButtonDisabled = isSubmitting ||
      Object.keys(errors).length > 0 ||
      !values.phoneConfirmed;

    return (
      <Grid container direction="column">
        <FormField
          label={translate('manageRegistration.clientPhone')}
          errors={errors}
          name="phone"
        >
          <MuiPhoneInput
            defaultCountry={values.domain.countryCode}
            value={values.phone}
            id="phone"
            onChange={(phone: string) => {
              handleChange('phone')(phone);
              handleChange('clientName')('');
              handleChange('clientEmail')(undefined);
              handleChange('studioClient')(undefined);
              handleChange('pendingClient')(undefined);
              handleChange('pin')(undefined);
              handleChange('phoneConfirmed')(false);
              return getClient(phone);
            }}
          />
        </FormField>

        {!!values.phoneConfirmed ? (
          <Typography className={classes.confirmed}>{translate('manageRegistration.phoneNumberConfirmed')}</Typography>
        ) : (
          <FormField
            label={translate('manageRegistration.pin')}
            errors={errors}
            name="pin"
          >
            <Grid container spacing={1}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  error={values.phoneConfirmed === false}
                  autoComplete="off"
                  inputProps={{ autofill: 'off' }}
                  id="pin"
                  value={values.pin}
                  onChange={onPinChanged}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Button
                  disabled={values.phoneConfirmed || !isPhoneValid}
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={(e) => values.phone && generatePin(e, values.phone)}
                >
                  {generatingPin ? LayoutHelper.renderLoader() : ''}
                  {values.studioClient
                    ? translate('manageRegistration.remindPin')
                    : translate('manageRegistration.sendMePinAgain')}
                </Button>
              </Grid>
              {showInfoMessage && (
                <Grid item xs={12}>
                  <InfoAlert
                    infoText={translate('manageRegistration.savePinInfo')}
                  />
                </Grid>
              )}
            </Grid>
          </FormField>
        )}

        {values.phoneConfirmed && (
          <Fragment>
            <FormField
              label={translate('manageRegistration.clientName')}
              errors={errors}
              name="clientName"
            >
              <TextField
                id="clientName"
                value={values.clientName}
                disabled={!!values.studioClient}
                onChange={(e) => handleChange('clientName')(e.target.value)}
                margin="normal"
              />
            </FormField>

            <FormField
              label={translate('manageRegistration.clientEmail')}
              errors={errors}
              name="clientEmail"
            >
              <TextField
                id="clientEmail"
                disabled={!!values.studioClient}
                value={values.clientEmail}
                onChange={handleEmailChange}
                margin="clientEmail"
              />
            </FormField>

            {classInfo.allowFriendSignUp && (
              <Fragment>
                <FormControl>
                  <FormControlLabel
                    name="withFriends"
                    control={
                      <Checkbox
                        checked={values.withFriends}
                        onChange={(event) => {
                          handleChange('withFriends')(event.target.checked);
                          handleChange('numberOfFriends')(undefined);
                        }}
                      />
                    }
                    label={translate('manageRegistration.withFriends')}
                  />
                </FormControl>

                {values.withFriends && (
                  <FormField
                    label={translate('manageRegistration.numberOfFriends')}
                    errors={errors}
                    name="numberOfFriends"
                  >
                    <TextField
                      select
                      value={values.numberOfFriends}
                      onChange={(e) => handleChange('numberOfFriends')(e.target.value)}
                    >
                      {Array.from({ length: spotsLeft - 1 }, (_, i) => (
                        <option key={i} value={i + 1}>{i + 1}</option>
                      ))}
                    </TextField>
                  </FormField>
                )}
              </Fragment>
            )}

            {/*<FormField label={translate("manageRegistration.clientNote")}*/}
            {/*           errors={errors}*/}
            {/*           name="clientNote">*/}
            {/*    <TextField*/}
            {/*        id="clientNote"*/}
            {/*        multiline*/}
            {/*        value={values.clientNote}*/}
            {/*        onChange={handleChange}*/}
            {/*        margin="clientNote"*/}
            {/*    />*/}
            {/*</FormField>*/}
            {!values.studioClient && (
              <Fragment>
                <BookingTermsAndConditions
                  accepted={values.tcAccepted}
                  onChange={(a) => handleChange('tcAccepted')(a)}
                  owner={owner}
                />
                <div style={{ marginBottom: '10px' }}>
                  <Recaptcha
                    onVerify={(canSubmit) => handleChange('captcha')(canSubmit)}
                  />
                </div>
              </Fragment>
            )}
          </Fragment>
        )}

        {course &&
          course.repeatableEvent &&
          course.repeatableEvent.beforeBookingText && (
            <SnackbarContent
              className={classes.errorMessage}
              message={
                <div
                  dangerouslySetInnerHTML={{
                    __html: course.repeatableEvent.beforeBookingText,
                  }}
                />
              }
            />
          )}

        <div className={classes.buttons}>
          <SignUpConfirmationButtons
            course={course}
            owner={owner}
            studioClient={values.studioClient}
            pendingClientId={values.pendingClient && values.pendingClient._id}
            disabled={isConfirmButtonDisabled}
            classInfo={classInfo}
            onSignUp={signUp}
            onPaymentFailed={onPaymentFailed}
            studio={studio}
            domain={{ domain: values.domain }}
          />
        </div>
      </Grid>
    );
  }
);